/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {createSelector} from 'reselect';
import {createDeepEqualSelector} from '@ion/applications/src/store/selectors/SelectorUtils';

// input selectors ...
const selectGridColumnState = ({adminNotifications: {grid}}) => grid.columnState;
const selectGridFilterModel = ({adminNotifications: {grid}}) => grid.filterModel;
const selectModularSearch   = ({adminNotifications: {search}}) => search;
const selectEditorMode      = ({adminNotifications: {editorMode}}) => editorMode;

const selectAdd          = ({adminNotifications: {add}}) => add;
const selectAddResult    = state => selectAdd(state).result;
const selectAddLoading   = state => selectAdd(state).loading;
const selectAddIsEditing = state => selectAdd(state).isEditing;

const selectEdit              = ({adminNotifications: {edit}}) => edit;
const selectEditResult        = state => selectEdit(state).result;
const selectEditLoading       = state => selectEdit(state).loading;
const selectEditIsEditing     = state => selectEdit(state).isEditing;
const selectEditIsViewMode    = state => selectEdit(state).isViewMode;
const selectEditSelected      = state => selectEdit(state).selected;
const selectEditActions       = state => selectEdit(state).actions;
const selectEditConfiguration = state => selectEdit(state).configuration;

const selectDestroy          = ({adminNotifications: {destroy}}) => destroy;
const selectDestroyResult    = state => selectDestroy(state).result;
const selectDestroySelected  = state => selectDestroy(state).selected;
const selectDestroyIsEditing = state => selectDestroy(state).isEditing;
const selectDestroyLoading   = state => selectDestroy(state).loading;
const selectDestroyError     = state => selectDestroy(state).error;

const selectBulkEdit          = ({adminNotifications: {bulkEdit}}) => bulkEdit;
const selectBulkEditResult    = state => selectBulkEdit(state).result;
const selectBulkEditLoading   = state => selectBulkEdit(state).loading;
const selectBulkEditIsEditing = state => selectBulkEdit(state).isEditing;
const selectBulkEditSelected  = state => selectBulkEdit(state).selected;

const selectBulkDelete          = ({adminNotifications: {bulkDelete}}) => bulkDelete;
const selectBulkDeleteResult    = state => selectBulkDelete(state).result;
const selectBulkDeleteSelected  = state => selectBulkDelete(state).selected;
const selectBulkDeleteIsEditing = state => selectBulkDelete(state).isEditing;
const selectBulkDeleteLoading   = state => selectBulkDelete(state).loading;
const selectBulkDeleteError     = state => selectBulkDelete(state).error;
const selectBulkDeleteRecord    = state => selectBulkDelete(state).record;

const selectAudit                  = ({adminNotifications: {audit}}) => audit;
const selectAuditSelected          = state => selectAudit(state).selected;
const selectAuditRecord            = state => selectAudit(state).auditRecord;
const selectAuditIsEditing         = state => selectAudit(state).isEditing;
const selectAuditShowChangesDialog = state => selectAudit(state).showChangesDialog;

// common output selectors
const resultSelector    = result => result;
const selectedSelector  = selected => selected;
const isEditingSelector = (isEditing: boolean) => isEditing;
const loadingSelector   = (loading: boolean) => loading;
const errorSelector     = error => error;
const recordSelector    = record => record;

// output selectors ...
export const adminNotificationsGridColumnStateSelector = createDeepEqualSelector(selectGridColumnState,
  columnState => columnState
);

export const adminNotificationsGridFilterModelSelector = createDeepEqualSelector(selectGridFilterModel,
  filterModel => filterModel
);

export const adminNotificationsEditorModeSelector = createSelector(selectEditorMode, editorMode => editorMode);

export const adminNotificationsAddResultSelector = createDeepEqualSelector(selectAddResult, resultSelector);

export const adminNotificationsAddLoadingSelector = createSelector(selectAddLoading, loadingSelector);

export const adminNotificationsAddIsEditingSelector = createSelector(selectAddIsEditing, isEditingSelector);

export const adminNotificationsEditResultSelector = createDeepEqualSelector(selectEditResult, resultSelector);

export const adminNotificationsEditLoadingSelector = createSelector(selectEditLoading, loadingSelector);

export const adminNotificationsEditIsEditingSelector = createSelector(selectEditIsEditing, isEditingSelector);

export const adminNotificationsEditIsViewModeSelector = createSelector(selectEditIsViewMode,
  (isViewMode: boolean) => isViewMode
);

export const adminNotificationsEditSelectedSelector = createDeepEqualSelector(selectEditSelected, selectedSelector);

export const adminNotificationsEditActionsSelector = createDeepEqualSelector(selectEditActions,
  actions => actions
);

export const adminNotificationsEditConfigurationSelector = createDeepEqualSelector(selectEditConfiguration,
  configuration => configuration
);

export const adminNotificationsDestroyResultSelector = createDeepEqualSelector(selectDestroyResult, resultSelector);

export const adminNotificationsDestroyLoadingSelector = createSelector(selectDestroyLoading, loadingSelector);

export const adminNotificationsDestroyIsEditingSelector = createSelector(selectDestroyIsEditing, isEditingSelector);

export const adminNotificationsDestroySelectedSelector = createDeepEqualSelector(
  selectDestroySelected, selectedSelector
);

export const adminNotificationsDestroyErrorSelector = createDeepEqualSelector(selectDestroyError, errorSelector);

export const adminNotificationsBulkEditResultSelector = createDeepEqualSelector(selectBulkEditResult, resultSelector);

export const adminNotificationsBulkEditSelectedSelector = createDeepEqualSelector(
  selectBulkEditSelected,
  selectedSelector
);

export const adminNotificationsBulkEditLoadingSelector = createSelector(selectBulkEditLoading, loadingSelector);

export const adminNotificationsBulkEditIsEditingSelector = createSelector(selectBulkEditIsEditing, isEditingSelector);

export const adminNotificationsBulkDeleteResultSelector = createDeepEqualSelector(
  selectBulkDeleteResult,
  resultSelector
);

export const adminNotificationsBulkDeleteLoadingSelector = createSelector(selectBulkDeleteLoading, loadingSelector);

export const adminNotificationsBulkDeleteIsEditingSelector = createSelector(
  selectBulkDeleteIsEditing,
  isEditingSelector)
;

export const adminNotificationsBulkDeleteSelectedSelector = createDeepEqualSelector(
  selectBulkDeleteSelected,
  selectedSelector
);

export const adminNotificationsBulkDeleteRecordSelector = createDeepEqualSelector(
  selectBulkDeleteRecord,
  recordSelector
);

export const adminNotificationsBulkDeleteErrorSelector = createDeepEqualSelector(selectBulkDeleteError, errorSelector);

export const adminNotificationsAuditSelectedSelector = createDeepEqualSelector(selectAuditSelected, selectedSelector);

export const adminNotificationsAuditRecordSelector = createDeepEqualSelector(selectAuditRecord, recordSelector);

export const adminNotificationsAuditIsEditingSelector = createSelector(selectAuditIsEditing, isEditingSelector);

export const adminNotificationsAuditShowChangesDialog = createSelector(selectAuditShowChangesDialog,
  (showChangesDialog: boolean) => showChangesDialog
);

export const adminNotificationsModularSearchSelector = createDeepEqualSelector(selectModularSearch,
  search => search
);
